'use client'

import { cn } from '@mentora/ui-components'
import { Star } from 'lucide-react'

interface StarRatingProps {
  value: number
  /** When provided the stars become interactive buttons (rating input). */
  onChange?: (value: number) => void
  size?: number
  className?: string
  label?: (value: number) => string
}

/**
 * Five-star rating. Read-only when `onChange` is omitted (display), an
 * interactive picker when provided (review form). Colors come from semantic
 * tokens so it themes per storefront.
 */
export function StarRating({
  value,
  onChange,
  size = 16,
  className,
  label,
}: StarRatingProps) {
  const interactive = typeof onChange === 'function'
  const rounded = Math.round(value)

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= rounded
        const star = (
          <Star
            style={{ width: size, height: size }}
            className={cn(
              filled
                ? 'fill-[var(--color-warning)] text-[var(--color-warning)]'
                : 'text-[var(--color-border)]',
            )}
          />
        )
        if (!interactive)
          return <span key={n}>{star}</span>
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange?.(n)}
            aria-label={label?.(n) ?? String(n)}
            className="rounded-sm p-0.5 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)]"
          >
            {star}
          </button>
        )
      })}
    </div>
  )
}
